import {
	Box,
	Dialog,
	DialogActions,
	DialogContent,
	DialogProps,
	DialogTitle,
	IconButton,
	useTheme,
} from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import { mergeSx } from "src/utils/styles";
import React from "react";
import { faSpinnerThird, faTimes } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { AI_ASSISTANT_PANEL_NO_RESERVED_WIDTH } from "src/components/views/ai/assistant/panel/AiAssistantPanelConstants.ts";
import { useOptionalAiAssistant } from "src/components/views/ai/assistant/AiAssistantContext.tsx";
import { useShowTestEnvironmentWarning } from "src/components/common/testEnvironmentWarning/useShowTestEnvironmentWarning.ts";
import { TestEnvironmentWarningText } from "src/components/common/testEnvironmentWarning/TestEnvironmentWarningText.tsx";
import { AiAssistantOpenButton } from "src/components/views/ai/assistant/AiAssistantOpenButton.tsx";
import {
	registerAavoDialogStackEntry,
	shouldCloseAavoDialogOnExternalEscape,
} from "src/components/common/dialogs/aavoDialogStack.ts";
import { getAavoDialogAiAssistantPlacement } from "src/components/common/dialogs/aavoDialogAiAssistantPlacement.ts";

export type AavoDialogSize = "sm" | "md" | "lg" | "xl" | "fullscreen";

export interface AavoDialogProps extends Omit<
	DialogProps,
	"fullWidth" | "fullScreen" | "maxWidth" | "title" | "open" | "onClose" | "dialogSize"
> {
	size?: AavoDialogSize;
	fullScreenOnMobile?: boolean;
	coverAiAssistant?: boolean;
	title: React.ReactNode;
	actions?: React.ReactNode;
	cancelButton?: boolean;
	onClose: (reason: CloseReason) => unknown | Promise<unknown>;
	disableEnforceFocus?: boolean;
	disableEscapeKeyDown?: boolean;
}

type CloseReason = "backdropClick" | "escapeKeyDown" | "browserBackButton";

export const AavoDialog = ({
	size = "fullscreen",
	fullScreenOnMobile,
	coverAiAssistant = false,
	title,
	children,
	actions,
	onClose: onCloseProp,
	slotProps,
	disableEnforceFocus,
	disableEscapeKeyDown,
	sx,
	...other
}: AavoDialogProps) => {
	const theme = useTheme();
	const smallScreen = useMediaQuery(theme.breakpoints.down("md"));
	const smallModal = size === "sm";
	const fullScreen = size === "fullscreen" || (smallScreen && !smallModal) || (fullScreenOnMobile && smallScreen);
	const aiAssistant = useOptionalAiAssistant();
	const aiAssistantReservedWidth = aiAssistant?.reservedWidth ?? AI_ASSISTANT_PANEL_NO_RESERVED_WIDTH;
	const reservedWidth = coverAiAssistant ? AI_ASSISTANT_PANEL_NO_RESERVED_WIDTH : aiAssistantReservedWidth;
	const hasReservedSidePanelWidth = !smallScreen && reservedWidth !== AI_ASSISTANT_PANEL_NO_RESERVED_WIDTH;
	const aiAssistantPlacement = getAavoDialogAiAssistantPlacement({
		isAiAssistantOpen: aiAssistant?.isOpen ?? false,
		aiAssistantPanelMode: aiAssistant?.panelMode ?? "panel",
		coverAiAssistant,
	});
	const backdropSlotProps = typeof slotProps?.backdrop === "function" ? undefined : slotProps?.backdrop;
	const paperSlotProps = typeof slotProps?.paper === "function" ? undefined : slotProps?.paper;
	// A visible AI assistant must keep the focus while the user types into it.
	const shouldDisableEnforceFocus = disableEnforceFocus ?? aiAssistantPlacement !== "hidden";
	// A fullscreen AI assistant hides the dialog, so its Escape presses must not close the dialog.
	const shouldCloseOnExternalEscape = shouldDisableEnforceFocus && aiAssistantPlacement !== "above";

	const showTestEnvironmentWarning = useShowTestEnvironmentWarning() && !smallModal;

	const [isClosing, setIsClosing] = React.useState(false);
	const dialogIdRef = React.useRef(crypto.randomUUID());
	const dialogRootRef = React.useRef<HTMLDivElement | null>(null);

	const onClose = async (reason: CloseReason) => {
		try {
			setIsClosing(true);
			await onCloseProp(reason);
		} finally {
			setIsClosing(false);
		}
	};
	const onCloseRef = React.useRef(onClose);
	onCloseRef.current = onClose;

	const onMuiClose: DialogProps["onClose"] = async (_event, reason) => {
		if (reason === "escapeKeyDown" && disableEscapeKeyDown === true) return;
		await onClose(reason);
	};

	React.useEffect(
		() =>
			registerAavoDialogStackEntry({
				id: dialogIdRef.current,
				containsTarget: (target) => target instanceof Node && dialogRootRef.current?.contains(target) === true,
			}),
		[],
	);

	React.useEffect(() => {
		if (!shouldCloseOnExternalEscape || disableEscapeKeyDown === true) return;

		const onKeyDown = (event: KeyboardEvent) => {
			if (
				event.key !== "Escape" ||
				event.defaultPrevented ||
				!shouldCloseAavoDialogOnExternalEscape(dialogIdRef.current, event.target)
			) {
				return;
			}

			event.preventDefault();
			void onCloseRef.current("escapeKeyDown");
		};

		document.addEventListener("keydown", onKeyDown);
		return () => document.removeEventListener("keydown", onKeyDown);
	}, [disableEscapeKeyDown, shouldCloseOnExternalEscape]);

	return (
		<Dialog
			ref={dialogRootRef}
			fullWidth={true}
			fullScreen={fullScreen}
			maxWidth={fullScreen ? false : size}
			disableEnforceFocus={shouldDisableEnforceFocus}
			sx={mergeSx(
				fullScreen &&
					!smallScreen && {
						padding: 1,
					},
				coverAiAssistant && {
					zIndex: theme.zIndex.modal + 2,
				},
				hasReservedSidePanelWidth && {
					right: reservedWidth,
					transition: theme.transitions.create("right", {
						easing: theme.transitions.easing.sharp,
						duration: theme.transitions.duration.leavingScreen,
					}),
				},
				sx,
			)}
			onClose={onMuiClose}
			open={true}
			slotProps={{
				...slotProps,
				backdrop: {
					...backdropSlotProps,
					sx: mergeSx(
						hasReservedSidePanelWidth && {
							right: reservedWidth,
							transition: theme.transitions.create("right", {
								easing: theme.transitions.easing.sharp,
								duration: theme.transitions.duration.leavingScreen,
							}),
						},
						backdropSlotProps?.sx,
					),
				},
				paper: {
					...paperSlotProps,
					sx: mergeSx(
						getPaperSxForSize(size, smallScreen),
						!smallScreen && {
							borderRadius: "10px",
							boxShadow: "0 8px 32px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.08)",
						},
						showTestEnvironmentWarning && {
							border: "3px solid",
							borderColor: "error.main",
						},
						paperSlotProps?.sx,
					),
				},
			}}
			{...other}
		>
			<DialogTitle
				sx={mergeSx(
					{
						display: "flex",
						alignItems: "center",
						gap: 0.5,
						paddingX: 2,
						paddingY: smallModal ? 1 : 0.75,
						marginBottom: smallModal ? 1 : 0,
						borderBottom: "1px solid",
						borderColor: "divider",
						backgroundColor: "grey.50",
					},

					showTestEnvironmentWarning && {
						borderBottom: "3px solid",
						borderColor: "error.main",
					},
				)}
			>
				<Box
					sx={{
						color: "primary.main",
						overflow: "hidden",
						textOverflow: "ellipsis",
						whiteSpace: "nowrap",
						fontSize: (theme) => theme.typography.body2.fontSize,
						textTransform: "uppercase",
						fontWeight: 500,
						letterSpacing: "0.06em",
						flex: 1,
						minWidth: 0,
					}}
				>
					{title}
				</Box>
				{showTestEnvironmentWarning && <TestEnvironmentWarningText />}
				<AiAssistantOpenButton sx={{ color: "text.secondary", padding: 0.5 }} />
				<IconButton
					onClick={() => {
						onClose?.("escapeKeyDown");
					}}
					sx={{
						padding: 0.5,
						color: "text.secondary",
						transition: "color 0.15s ease, background-color 0.15s ease",
						"&:hover": {
							color: "text.primary",
							backgroundColor: "action.hover",
						},
					}}
				>
					{isClosing ?
						<FontAwesomeIcon icon={faSpinnerThird} spin fixedWidth />
					:	<FontAwesomeIcon icon={faTimes} fixedWidth />}
				</IconButton>
			</DialogTitle>
			<DialogContent
				sx={{
					display: "flex",
					margin: 0,
					padding: 0,
					overflow: "hidden",
				}}
			>
				{children}
			</DialogContent>
			<DialogActions
				disableSpacing={true}
				sx={{
					display: !actions ? "none" : undefined,
					gap: "0.5rem",
				}}
			>
				{actions}
			</DialogActions>
		</Dialog>
	);
};

const getPaperSxForSize = (size: AavoDialogSize, isSmallScreen: boolean) => {
	if (isSmallScreen) return {};

	switch (size) {
		case "sm":
			return {
				minHeight: "200px",
			};
		case "md":
			return {
				height: "700px",
			};
		case "lg":
			return {
				height: "800px",
			};
		case "xl":
			return {
				height: "1000px",
			};
		case "fullscreen":
			return {
				height: "100%",
			};
	}
};
